import {
  toWSDateString,
  useWSModal,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInfoBox,
  WSInputDate,
  WSInputTextarea,
  WSList,
  WSPill,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { DataItem } from "../../../../shared/components/DataItem";
import { useGetNow } from "../../../../shared/hooks/useGetNow";
import { addDays, getStartOfDay } from "../../../../utils/dates";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

import {
  expirationExtensionOptions,
  extendExpirationDefaultValues,
  ExtendExpirationFormData,
  getExtendExpirationDate,
  getExtendExpirationValidationSchema
} from "./utils";

interface ExtendExpirationDateModalProps {
  onClose: (success?: boolean) => void;
  onSuccess: (requirement: PayeeRequirement) => void;
  requirement: PayeeRequirement;
}

const ExtendExpirationDateModal: React.FC<ExtendExpirationDateModalProps> = ({
  onClose,
  onSuccess,
  requirement
}) => {
  const now = useGetNow();

  const [extendExpiration, extendExpirationMeta] =
    queriesRequirements.extendPayeeRequirementExpiration.useMutation();

  const xWingspanPayerId = useXWingspanPayerId();

  const currentExpirationDate = requirement.expiresAt;

  return (
    <WSForm<ExtendExpirationFormData>
      defaultValues={extendExpirationDefaultValues}
      validationSchema={getExtendExpirationValidationSchema(
        currentExpirationDate
      )}
      onSubmit={async formData => {
        const nextDate = getExtendExpirationDate(
          formData.extensionType,
          formData.customDate
        );

        await extendExpiration(
          {
            requirementId: requirement.id!,
            extendPayeeRequirementExpirationRequest: {
              expiresAt: nextDate!,
              reason: formData.reason
            },
            xWingspanAccount: xWingspanPayerId
          },
          {
            onSuccess: response => {
              onSuccess(response);
              onClose(true);
            }
          }
        );
      }}
    >
      {({ watch }) => {
        const extensionType = watch("extensionType");
        const customDate = watch("customDate");

        const nextDate = getExtendExpirationDate(extensionType, customDate);

        const isNextDateEarlier =
          nextDate && currentExpirationDate && nextDate < currentExpirationDate;

        const isNextDate90DaysLate = nextDate && nextDate > addDays(now, 90);

        return (
          <WSList gap="L">
            <WSInfoBox>
              <DataItem
                label="Current expiration date"
                value={toWSDateString(currentExpirationDate, "input")}
              />
              <WSText.ParagraphSm color="gray700" mt="L">
                As the client, you have the ability to extend a contractor's
                requirement expiration date by overriding it. Once extended, it
                cannot be reverted. It is recommended that you confirm with the
                contractor that there is already a renewal in progress to avoid
                you and/or the contractor falling out of compliance upon the
                expiration date.
              </WSText.ParagraphSm>
            </WSInfoBox>

            <WSElement>
              <WSForm.Field
                component={WSSelect}
                componentProps={{
                  options: expirationExtensionOptions
                }}
                label="New expiration date"
                name="extensionType"
              />
            </WSElement>
            <WSInfoBox>
              {extensionType === "custom" && (
                <WSForm.Field
                  component={WSInputDate}
                  componentProps={{
                    minDate: getStartOfDay(1)
                  }}
                  label="Custom expiration date"
                  mb="L"
                  name="customDate"
                />
              )}

              {nextDate && (
                <DataItem
                  label="New expiration date"
                  value={toWSDateString(nextDate, "input")}
                />
              )}
            </WSInfoBox>

            {isNextDateEarlier ? (
              <WSAlert
                theme="warning"
                title="About moving the expiration earlier"
              >
                You are about to move the expiration to an earlier date than
                than the original. Please ensure that the necessary
                communication has been provided to the contractor to avoid
                unexpected surprises and payment delays.
              </WSAlert>
            ) : isNextDate90DaysLate ? (
              <WSAlert
                theme="warning"
                title="New expiration date is over 90 days out"
              >
                You are about to move the expiration to a future date over 90
                days out. Please double check the new expiration date.
                Incomplete requirements may result in payment delays.
              </WSAlert>
            ) : null}

            <WSElement>
              <WSFlexBox.CenterY gap="S" mb="M">
                <WSText.ParagraphSm color="gray500">Reason</WSText.ParagraphSm>
                <WSPill icon="lock" text="Private" theme="neutral" />
              </WSFlexBox.CenterY>

              <WSForm.Field
                component={WSInputTextarea}
                componentProps={{
                  placeholder:
                    "Please provide a reason for the change (optional)."
                }}
                name="reason"
              />
              <WSText.ParagraphXs color="gray400" mt="XS">
                Provide a reason for the change (visible to you and your team
                only).
              </WSText.ParagraphXs>
            </WSElement>

            <ErrorAlert context="Other" error={extendExpirationMeta.isError} />

            <WSActions
              buttons={[
                {
                  label: "Cancel",
                  kind: "Secondary",
                  onClick: () => onClose(false),
                  type: "button"
                },
                {
                  label: "Save changes",
                  kind: "Primary",
                  type: "submit",
                  loading: extendExpirationMeta.isLoading
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSForm>
  );
};

export function useExtendExpirationDateModal() {
  return useWSModal(ExtendExpirationDateModal, {
    title: "Update expiration date",
    size: "M"
  });
}
